const RANGE_DAYS = { week: 7, month: 30, year: 365, all: null };

export const SPARKLINE_TEST_CODES = ["kh_alk", "magnesium", "salinity"];

export function filterPointsByRange(data, range) {
  if (!Array.isArray(data) || data.length === 0) return [];
  if (range === "all") return data;
  const days = RANGE_DAYS[range];
  if (!days) return data;
  const maxTs = Math.max(...data.map((p) => p.date));
  const minTs = maxTs - days * 24 * 60 * 60 * 1000;
  return data.filter((p) => p.date >= minTs && p.date <= maxTs);
}

export function buildTrendDataset(chartSeries, hiddenCodes, range) {
  const visible = chartSeries.filter(
    (entry) => !hiddenCodes.includes(entry.def.code) && entry.data.length > 0,
  );
  if (visible.length === 0) {
    return { dataset: [], series: [] };
  }

  const dateSet = new Set();
  visible.forEach((entry) => {
    filterPointsByRange(entry.data, range).forEach((p) => dateSet.add(p.date));
  });
  const sortedDates = [...dateSet].sort((a, b) => a - b);
  if (sortedDates.length === 0) {
    return { dataset: [], series: [] };
  }

  const dataset = sortedDates.map((date) => {
    const row = { date: new Date(date) };
    visible.forEach((entry) => {
      const point = entry.data.find((p) => p.date === date);
      if (point == null || !Number.isFinite(point.value)) return;
      row[entry.def.code] = point.value;
      const raw =
        point.rawValue != null && Number.isFinite(point.rawValue)
          ? point.rawValue
          : point.value;
      row[`${entry.def.code}__raw`] = raw;
    });
    return row;
  });

  const series = visible.map((entry) => ({
    dataKey: entry.def.code,
    showMark: false,
    connectNulls: true,
    area: false,
  }));

  return { dataset, series, visible };
}

/** Always KH, magnesium, salinity — in that order (may have empty data). */
export function pickSparklineEntries(chartSeries) {
  return SPARKLINE_TEST_CODES.map(
    (code) => chartSeries.find((e) => e.def.code === code) ?? null,
  ).filter(Boolean);
}

/** Target line sits at the vertical center; outliers don't stretch the axis. */
const RELATIVE_TARGET_CENTER = 100;
const RELATIVE_MIN_HALF_SPAN = 12;
const RELATIVE_MAX_HALF_SPAN = 55;
/** How far from target (in %-points) a point can influence axis range. */
const RELATIVE_AXIS_INFLUENCE_CAP = 45;

export function computeRelativeYDomain(dataset, visibleCodes, center = RELATIVE_TARGET_CENTER) {
  const values = [];
  dataset.forEach((row) => {
    visibleCodes.forEach((code) => {
      const v = row[code];
      if (Number.isFinite(v)) values.push(v);
    });
  });

  if (values.length === 0) {
    return {
      min: center - RELATIVE_MIN_HALF_SPAN,
      max: center + RELATIVE_MIN_HALF_SPAN,
    };
  }

  const influenceSpans = values.map((v) =>
    Math.min(Math.abs(v - center), RELATIVE_AXIS_INFLUENCE_CAP),
  );
  const span = Math.min(
    RELATIVE_MAX_HALF_SPAN,
    Math.max(RELATIVE_MIN_HALF_SPAN, Math.max(...influenceSpans) * 1.1 + 2),
  );

  return { min: center - span, max: center + span };
}

export function formatMeasuredValue(raw, unit) {
  if (!Number.isFinite(raw)) return "—";
  const abs = Math.abs(raw);
  const decimals = abs < 1 ? 2 : abs < 10 ? 1 : 0;
  const text = Number(raw).toFixed(decimals);
  return unit ? `${text} ${unit}`.trim() : text;
}

/** Latest reading + % of tank target for legend / summary cards. */
export function getLatestReadingMeta(entry, targetByCode) {
  if (!entry?.data?.length) {
    return { valueText: "—", percentText: null };
  }
  const latest = entry.data[entry.data.length - 1];
  const raw =
    latest.rawValue != null && Number.isFinite(latest.rawValue)
      ? latest.rawValue
      : latest.value;
  const unit = entry.def?.primaryUnit || "";
  const target = targetByCode.get(entry.def.code);
  const valueText = formatMeasuredValue(raw, unit);

  if (!Number.isFinite(target) || target <= 0 || !Number.isFinite(raw)) {
    return { valueText, percentText: null };
  }

  const percentText = `${((raw / target) * 100).toFixed(0)}% of target`;
  return { valueText, percentText };
}
